import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PropertyReportContent } from '../components/reports/PropertyReportContent';
import { getPropertyById } from '../services/properties';
import { getMonthlyStats } from '../services/stats';
import { Button } from '../components/ui/Button';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { generatePropertyReport } from '../utils/pdf/propertyReport';
import { Property } from '../types/user';
import { MonthlyStats } from '../types/property';
import toast from 'react-hot-toast';

export const PropertyReportPage = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const [propertyData, statsData] = await Promise.all([
          getPropertyById(id),
          getMonthlyStats(id)
        ]);

        if (!propertyData) {
          throw new Error('Propiedad no encontrada');
        }

        setProperty(propertyData);
        setMonthlyStats(statsData);
      } catch (error) {
        console.error('Error al cargar los datos:', error);
        toast.error('Error al cargar los datos de la propiedad');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDownloadPDF = async () => {
    try {
      if (!property) {
        toast.error('No hay datos para generar el informe');
        return;
      }

      const doc = generatePropertyReport(property, monthlyStats);
      const fileName = `informe_${property.name.toLowerCase().replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);
      toast.success('Informe descargado correctamente');
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      toast.error('Error al generar el informe PDF');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <div className="text-center text-gray-500">
          No se encontró la propiedad
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Informe de {property.name}</h1>
          <Button onClick={handleDownloadPDF} className="flex items-center space-x-2">
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>Descargar PDF</span>
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <PropertyReportContent property={property} monthlyStats={monthlyStats} />
        </div>
      </div>
    </DashboardLayout>
  );
};